import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { NotificationApiService } from './notification-api.service';
import { PortalAuthService } from '../auth/portal-auth.service';

/**
 * Derives per-nav-item badge counts from the user's *unread* notifications.
 *
 * The notification bell (NotificationApiService) already owns the list; this
 * groups the unread entries by feature area (via their NotificationType prefix)
 * and exposes a `url → count` map that the side menu binds to. No new backend:
 * it reuses `GET /notifications`. Refreshed whenever the menu opens
 * (see SideMenuComponent.onMenuOpen) so counts stay in step with reads.
 *
 * A badge means "someone else changed this while you weren't looking", so the
 * reader's own actions are skipped — see `refresh`. The notification history in the
 * bell still lists them; only the count is filtered.
 */
@Injectable({ providedIn: 'root' })
export class NavBadgeService {
  /**
   * NotificationType prefix → target nav url. First match wins, so longer prefixes must
   * come before any shorter one they start with.
   *
   * Types with no entry deliberately badge nothing (they have no page of their own) but
   * still count toward the bell — the two numbers are not meant to agree.
   */
  private static readonly TYPE_TO_URL: ReadonlyArray<readonly [string, string]> = [
    ['Reservation', '/tablestack'],
    ['TableStack', '/tablestack'],
    ['Invoice', '/invoices'],
    ['Rechnung', '/invoices'],
    ['Ticket', '/support'],
    // Member* is tenant *access* (invite/update/revoke), managed on /users. Employee* is
    // personnel data, a different page — the two were previously both sent to /employees,
    // which badged Personaldaten every time somebody was invited.
    ['Member', '/users'],
    ['Employee', '/employees'],
    ['JobPosition', '/departments'],
    ['Department', '/departments'],
    ['Menu', '/menus'],
    ['Gateway', '/devices'],
    ['Permissions', '/access'],
    ['Role', '/access'],
    ['Document', '/documents'],
  ];

  private readonly _counts$ = new BehaviorSubject<Record<string, number>>({});
  readonly counts$ = this._counts$.asObservable();

  /** Auth0 sub of the signed-in member; null until the profile resolves. */
  private currentUserSub: string | null = null;

  constructor(
    private readonly notifications: NotificationApiService,
    portalAuth: PortalAuthService,
  ) {
    portalAuth.user$.subscribe(user => { this.currentUserSub = user?.sub ?? null; });
  }

  /** Re-tally unread notifications into per-url counts. Safe to call repeatedly. */
  refresh(): void {
    this.notifications.getAll().subscribe({
      next: list => {
        const counts: Record<string, number> = {};
        for (const n of list) {
          if (n.isRead) continue;
          // You just did this yourself — badging it would mean the sidebar lights up on
          // your own save. Anything from another member, and anything system-generated
          // (no issuedBy), still counts, whether or not you were online when it happened.
          if (this.currentUserSub && n.issuedBy === this.currentUserSub) continue;
          const url = NavBadgeService.resolve(n.type);
          if (url) counts[url] = (counts[url] ?? 0) + 1;
        }
        this._counts$.next(counts);
      },
      error: () => this._counts$.next({}),
    });
  }

  countFor(url: string): Observable<number> {
    return this.counts$.pipe(map(c => c[url] ?? 0));
  }

  private static resolve(type: string): string | null {
    for (const [prefix, url] of NavBadgeService.TYPE_TO_URL) {
      if (type.startsWith(prefix)) return url;
    }
    return null;
  }
}
